// File: app/src/components/workout/MacroProgress.js
//
// Dumb 2D viewport for session-level progress. Two tiers:
//   1. Block pills (structural permanence) — always-on map of top-level phases.
//   2. Iteration dots (contextual expansion) — sets/rounds of the active block.
//
// The upstream state manager hands this component the exact shape it needs;
// no string parsing happens here.

import React, { useEffect, useRef } from "react";

import { getPhaseMotionTokens } from "../../utils/planVisuals";

// Static Tailwind class maps so JIT can see every possible class name.
const blockActiveMap = {
  work: "bg-work/20 border-work/40 text-work",
  rest: "bg-rest/20 border-rest/40 text-rest",
  warmup: "bg-warmup/20 border-warmup/40 text-warmup",
  cooldown: "bg-cooldown/20 border-cooldown/40 text-cooldown",
  group: "bg-group/20 border-group/40 text-group",
  strength: "bg-strength/20 border-strength/40 text-strength",
  hiit: "bg-hiit/20 border-hiit/40 text-hiit",
  cardio: "bg-cardio/20 border-cardio/40 text-cardio",
  mobility: "bg-mobility/20 border-mobility/40 text-mobility",
};

const dotWorkColourMap = {
  work: "bg-work",
  warmup: "bg-warmup",
  cooldown: "bg-cooldown",
  group: "bg-group",
  strength: "bg-strength",
  hiit: "bg-hiit",
  cardio: "bg-cardio",
  mobility: "bg-mobility",
};

const resolveTypeKey = (type) => {
  const lower = (type || "").toLowerCase();
  if (lower === "cooldown" || lower === "cool down") return "cooldown";
  if (lower === "warmup" || lower === "warm up") return "warmup";
  if (blockActiveMap[lower]) return lower;
  return "group";
};

// Pixels the pointer must travel before a press counts as a drag (pan) rather
// than a tap (open the block summary). Below this we let the click through.
const DRAG_THRESHOLD_PX = 4;

const BlockPills = ({ blocks, onBlockTap }) => {
  const scrollRef = useRef(null);
  const activeRef = useRef(null);
  // Mouse drag-to-pan bookkeeping. `moved` survives until the trailing click so
  // onClickCapture can swallow the click that a drag would otherwise trigger.
  const dragRef = useRef({ down: false, moved: false, startX: 0, startScroll: 0 });
  const activeIndex = blocks.findIndex((b) => b.state === "active");

  // Keep the active block centred as the session advances. The row can overflow
  // horizontally on long sessions; auto-centring the current phase keeps it
  // readable without the user scrolling. Scrolls only this container (not the
  // page) by nudging scrollLeft by the pill's offset from the centre. Issue #22.
  useEffect(() => {
    const el = activeRef.current;
    const container = scrollRef.current;
    if (!el || !container) return;
    const elRect = el.getBoundingClientRect();
    const cRect = container.getBoundingClientRect();
    const delta =
      elRect.left + elRect.width / 2 - (cRect.left + cRect.width / 2);
    container.scrollBy({ left: delta, behavior: "smooth" });
  }, [activeIndex, blocks.length]);

  // Click-drag to pan the row left/right (mouse only — touch/trackpad already
  // scroll natively via overflow-x-auto, and grabbing pointer events on touch
  // would kill momentum scrolling). Pointer capture only kicks in once the drag
  // threshold is crossed, so a plain click still lands on its pill/button.
  const handlePointerDown = (e) => {
    if (e.pointerType !== "mouse") return;
    const container = scrollRef.current;
    if (!container) return;
    dragRef.current = {
      down: true,
      moved: false,
      startX: e.clientX,
      startScroll: container.scrollLeft,
    };
  };

  const handlePointerMove = (e) => {
    const s = dragRef.current;
    const container = scrollRef.current;
    if (!s.down || !container) return;
    const dx = e.clientX - s.startX;
    if (!s.moved && Math.abs(dx) > DRAG_THRESHOLD_PX) {
      s.moved = true;
      container.setPointerCapture?.(e.pointerId);
    }
    if (s.moved) {
      container.scrollLeft = s.startScroll - dx;
      e.preventDefault();
    }
  };

  const handlePointerUp = (e) => {
    const s = dragRef.current;
    if (s.down && s.moved) {
      scrollRef.current?.releasePointerCapture?.(e.pointerId);
    }
    s.down = false;
  };

  // A real drag ends in a synthetic click on the pressed element; swallow it so
  // panning never also opens a block summary. Reset the flag for the next press.
  const handleClickCapture = (e) => {
    if (dragRef.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      dragRef.current.moved = false;
    }
  };

  return (
    <div
      ref={scrollRef}
      // Faded edges show the strip scrolls; without them the last pill read
      // as truncated text ("Back Squ").
      className="flex w-full overflow-x-auto no-scrollbar cursor-grab active:cursor-grabbing select-none [mask-image:linear-gradient(90deg,transparent,#000_10%,#000_90%,transparent)]"
      style={{ scrollbarWidth: "none" }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onClickCapture={handleClickCapture}
    >
      {/* Inner track is centred when it fits, scrollable when it overflows. */}
      <div className="flex items-center gap-1 mx-auto w-max">
        {blocks.map((block, i) => {
          const isActive = block.state === "active";
          const connector = i < blocks.length - 1 && (
            <div
              className={`w-2 h-px shrink-0 ${
                block.state === "completed" ? "bg-white/20" : "bg-white/10"
              }`}
            />
          );

          // Setup sections render as a compact chapter-marker dot — a bead on
          // the stepper string — rather than a full pill. The duration surfaces
          // on hover (native tooltip) so the row stays uncluttered. The active
          // setup dot brightens and pulses so the live phase is still legible.
          if (block.isSetup) {
            let dotClass = "w-1.5 h-1.5 rounded-full shrink-0 transition-colors";
            if (isActive) {
              dotClass += " bg-transition animate-pulse-fade";
            } else if (block.state === "completed") {
              dotClass += " bg-transition/50";
            } else {
              dotClass += " bg-transition/25";
            }
            const tip = block.durationLabel
              ? `${block.label} · ${block.durationLabel}`
              : block.label;
            return (
              <React.Fragment key={i}>
                <div
                  ref={isActive ? activeRef : undefined}
                  className={dotClass}
                  title={tip}
                  aria-label={tip}
                />
                {connector}
              </React.Fragment>
            );
          }

          const typeKey = resolveTypeKey(block.type);
          let pillClass =
            "px-2 py-0.5 rounded-full text-[10px] font-medium tracking-wide border transition-colors shrink-0";
          // The active pill is always fully legible — no truncation. Inactive
          // pills stay compact so the row doesn't crowd.
          pillClass += isActive
            ? " whitespace-nowrap max-w-none"
            : " truncate max-w-[88px]";
          if (isActive) {
            pillClass += ` ${blockActiveMap[typeKey] || blockActiveMap.group}`;
          } else if (block.state === "completed") {
            pillClass += " bg-white/5 border-transparent text-white/30";
          } else {
            pillClass += " border-white/10 text-white/20 bg-transparent";
          }
          if (onBlockTap) {
            pillClass +=
              " hover:brightness-125 focus:outline-none focus-visible:ring-1 focus-visible:ring-ring";
          }
          const PillTag = onBlockTap ? "button" : "div";
          const extraProps = onBlockTap
            ? {
                type: "button",
                onClick: () => onBlockTap(block, i),
                "aria-label": `Preview ${block.label}`,
              }
            : {};
          return (
            <React.Fragment key={i}>
              <PillTag
                ref={isActive ? activeRef : undefined}
                className={pillClass}
                {...extraProps}
              >
                {block.label}
              </PillTag>
              {connector}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

const IterationDots = ({ info, activeTypeKey }) => {
  const { label, totalIterations, currentIndex, phase } = info;
  const workColourClass = dotWorkColourMap[activeTypeKey] || "bg-group";
  const motionTokens = getPhaseMotionTokens(
    phase === "rest" ? "rest" : activeTypeKey,
  );
  return (
    <div className="flex items-center justify-center gap-2">
      {label && (
        <span className="text-[10px] font-medium tracking-wide text-white/40 uppercase">
          {label}
        </span>
      )}
      <div className="flex items-center gap-1.5">
        {Array.from({ length: totalIterations }).map((_, i) => {
          const isActive = i === currentIndex;
          const isCompleted = i < currentIndex;
          let dotClass = "w-2 h-2 rounded-full transition-colors";
          if (isActive) {
            dotClass +=
              phase === "rest"
                ? ` bg-rest ${motionTokens.dotPulseClass}`
                : ` ${workColourClass} ${motionTokens.dotPulseClass}`;
          } else if (isCompleted) {
            dotClass += " bg-white/25";
          } else {
            dotClass += " bg-white/10";
          }
          return (
            <div
              key={i}
              className={dotClass}
              style={{
                transitionDuration: `${motionTokens.dotTransitionMs}ms`,
                ...(isActive
                  ? { animationDuration: `${motionTokens.dotPulseMs}ms` }
                  : {}),
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

const MacroProgress = ({
  blocks = [],
  activeGroupInfo = null,
  onBlockTap = null,
}) => {
  const showPills = blocks.length > 1;
  const showDots = !!activeGroupInfo && activeGroupInfo.totalIterations > 1;

  if (!showPills && !showDots) return null;

  const activeBlock = blocks.find((b) => b.state === "active");
  const activeTypeKey = resolveTypeKey(activeBlock?.type);

  return (
    <div className="flex flex-col items-center gap-3 w-full px-2 mb-4">
      {showPills && <BlockPills blocks={blocks} onBlockTap={onBlockTap} />}
      {showDots && (
        <IterationDots info={activeGroupInfo} activeTypeKey={activeTypeKey} />
      )}
    </div>
  );
};

export default MacroProgress;
